import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Copy, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const RegexTesterTool = () => {
  const [pattern, setPattern] = useState('');
  const [flags, setFlags] = useState('g');
  const [testString, setTestString] = useState('');
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState('');

  const testRegex = () => {
    setError('');
    setMatches([]);
    
    if (!pattern) {
      toast.error('Please enter a regex pattern');
      return;
    }

    try {
      const regex = new RegExp(pattern, flags);
      const allMatches = [...testString.matchAll(regex)];
      setMatches(allMatches.map(m => ({
        match: m[0],
        index: m.index,
        groups: m.slice(1)
      })));
      toast.success(`Found ${allMatches.length} match(es)`);
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleFlag = (flag) => {
    if (flags.includes(flag)) {
      setFlags(flags.replace(flag, ''));
    } else {
      setFlags(flags + flag);
    }
  };

  const copyPattern = () => {
    navigator.clipboard.writeText(`/${pattern}/${flags}`);
    toast.success('Copied!');
  };

  const flagOptions = [
    { flag: 'g', label: 'Global' },
    { flag: 'i', label: 'Case insensitive' },
    { flag: 'm', label: 'Multiline' },
    { flag: 's', label: 'Dotall' },
  ];

  return (
    <div className="space-y-4" data-testid="regex-tester-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Regex Tester</h3>
      </div>

      <div className="space-y-2">
        <label className="text-sm text-muted-foreground">Pattern</label>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center bg-background/50 border border-border rounded-md px-3">
            <span className="text-muted-foreground">/</span>
            <Input
              placeholder="Enter regex pattern..."
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              className="border-0 bg-transparent font-mono"
              data-testid="pattern-input"
            />
            <span className="text-muted-foreground">/{flags}</span>
          </div>
          <Button variant="outline" onClick={copyPattern}>
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {flagOptions.map(({ flag, label }) => (
          <Button
            key={flag}
            variant={flags.includes(flag) ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleFlag(flag)}
          >
            {label} ({flag})
          </Button>
        ))}
      </div>

      <div className="space-y-2">
        <label className="text-sm text-muted-foreground">Test String</label>
        <Textarea
          placeholder="Enter text to test against..."
          value={testString}
          onChange={(e) => setTestString(e.target.value)}
          className="min-h-[100px] bg-background/50 font-mono"
          data-testid="test-string-input"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded bg-destructive/10 border border-destructive/30 text-destructive text-sm">
          <X className="w-4 h-4" />
          {error}
        </div>
      )}

      <Button onClick={testRegex} className="w-full" data-testid="test-btn">
        Test Regex
      </Button>

      {matches.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            <span className="text-sm">{matches.length} match(es) found</span>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {matches.map((m, i) => (
              <div key={i} className="p-2 rounded bg-background/50 border border-border">
                <div className="flex justify-between items-center">
                  <Badge variant="outline">Match {i + 1}</Badge>
                  <span className="text-xs text-muted-foreground">Index: {m.index}</span>
                </div>
                <div className="font-mono text-sm mt-1 text-primary">{m.match}</div>
                {m.groups.length > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Groups: {m.groups.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegexTesterTool;
